import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { formatQuantityParts } from '../utils/unitDisplay';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Check, Search, Plus, Star, Phone, Mail, Package, ArrowLeft, TrendingDown, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import MinimalStockService from '../services/minimalStockService';
import http from '../services/httpClient';
import './CreateReorder.css';
import { StandardScrollStyles, highlightMatch, performSmartSearch, defaultTextSizes, standardScrollStyles } from '../styles/standardStyles';
import { useTranslate } from '../translations/useTranslate';
import { leakageApi, inr, pct, monthLabel } from './leakageMonthly/api';

// Using lucide-react's ArrowLeft icon directly

const CreateReorder = ({ onNavigateBack, onNavigateToVendor, selectedVendor, onClearSelectedVendor, goTo }) => {
    
    const { t } = useTranslation();
    const { language } = useTranslate(); // Get current language for API calls
    
    // Helper function to normalize category names to match VendorManagement filters
    const normalizeCategoryName = (category) => {
        if (!category) return 'All';
        const normalized = category.toString().toLowerCase();
        const categoryMap = {
            'wholesale': 'Wholesale',
            'dairy': 'Dairy', 
            'meat': 'Meat',
            'seafood': 'Seafood',
            'vegetables': 'Vegetables',
            'fruits': 'Fruits'
        };
        const result = categoryMap[normalized] || 'All';
        return result;
    };

    // Canonicalize vendor categories for uniqueness and consistent matching
    const canonicalizeVendorCategory = useCallback((rawCategory) => {
        const s = (rawCategory || '').toString().trim().toLowerCase();
        if (!s) return { key: 'other', label: 'Other' };
        // common synonyms
        const map = [
            { keys: ['auto ingredients'], key: 'wholesale', label: 'Wholesale' },
            { keys: ['wholesale', 'general', 'grocery', 'staples'], key: 'wholesale', label: 'Wholesale' },
            { keys: ['dairy', 'milk', 'curd', 'paneer', 'cheese', 'butter'], key: 'dairy', label: 'Dairy' },
            { keys: ['meat', 'butcher', 'chicken', 'mutton', 'poultry'], key: 'meat', label: 'Meat' },
            { keys: ['seafood', 'sea food', 'fish', 'prawn', 'prawns'], key: 'seafood', label: 'Seafood' },
            { keys: ['vegetables', 'veg', 'produce', 'greens'], key: 'vegetables', label: 'Vegetables' },
            { keys: ['fruits', 'fruit'], key: 'fruits', label: 'Fruits' },
            { keys: ['spices', 'masala', 'masalas'], key: 'spices', label: 'Spices' },
            { keys: ['grains', 'pulses', 'flour', 'atta', 'rice', 'dal', 'millets'], key: 'grains', label: 'Grains' },
            { keys: ['bakery', 'bread'], key: 'bakery', label: 'Bakery' },
            { keys: ['beverages', 'drinks', 'juice', 'tea', 'coffee'], key: 'beverages', label: 'Beverages' },
            { keys: ['oils', 'oil'], key: 'oils', label: 'Oils' },
            { keys: ['condiments', 'sauces', 'ketchup', 'chutney'], key: 'condiments', label: 'Condiments' },
            { keys: ['snacks', 'namkeen'], key: 'snacks', label: 'Snacks' },
            { keys: ['frozen'], key: 'frozen', label: 'Frozen' },
        ];
        for (const m of map) {
            if (m.keys.some(k => s.includes(k))) return { key: m.key, label: m.label };
        }
        // default: title case the raw input
        const label = s.replace(/\b\w/g, (c) => c.toUpperCase());
        const key = s.replace(/[^a-z0-9]+/g, '-');
        return { key: key || 'other', label: label || 'Other' };
    }, []);

    // Helper to attach normalized category metadata to a vendor object
    const withNormalizedCategory = useCallback((vendor, categoryOverride = null) => {
        const raw = (categoryOverride || vendor.vendor_category || vendor.category || '').toString();
        const { key, label } = canonicalizeVendorCategory(raw);
        return {
            ...vendor,
            normalizedCategoryKey: key,
            normalizedCategoryLabel: label,
            // Keep a consistent category field for display/search when missing
            category: vendor.category || vendor.vendor_category || label,
            vendor_category: vendor.vendor_category || vendor.category || label,
        };
    }, [canonicalizeVendorCategory]);

    const dedupeByCategory = useCallback((list) => {
        const seen = new Set();
        const out = [];
        for (const v of list) {
            const key = (v.normalizedCategoryKey || '').toString();
            if (!seen.has(key)) {
                seen.add(key);
                out.push(v);
            }
        }
        return out;
    }, []);

    // Derive a vendor category key for an item using name overrides first, then category synonyms
    const deriveVendorCategoryKey = useCallback((inventoryCategory, itemName = '') => {
        const name = (itemName || '').toString().toLowerCase();
        // Item-name overrides
        const nameOverrides = [
            { k: 'chicken', key: 'meat' },
            { k: 'beef', key: 'meat' },
            { k: 'mutton', key: 'meat' },
            { k: 'lamb', key: 'meat' },
            { k: 'pork', key: 'meat' },
            { k: 'fish', key: 'seafood' },
            { k: 'prawn', key: 'seafood' },
            { k: 'prawns', key: 'seafood' },
            { k: 'shrimp', key: 'seafood' },
            { k: 'crab', key: 'seafood' },
            { k: 'lobster', key: 'seafood' },
            { k: 'milk', key: 'dairy' },
            { k: 'paneer', key: 'dairy' },
            { k: 'cheese', key: 'dairy' },
            { k: 'butter', key: 'dairy' },
            { k: 'yogurt', key: 'dairy' },
            { k: 'curd', key: 'dairy' },
        ];
    const processedKeywords = ['masala', 'powder', 'mix', 'premix', 'soup', 'broth', 'stock', 'seasoning', 'spice', 'spices', 'gravy', 'base', 'paste', 'pepper', 'tikka', 'tandoori', 'marinade', 'marination', 'rub', 'coating', 'batter'];
        const isProcessed = processedKeywords.some(k => name.includes(k));
        if (!isProcessed) {
            const hit = nameOverrides.find(o => name.includes(o.k));
            if (hit) return hit.key;
        }

        // Fallback to category-based normalization
        const cat = (inventoryCategory || '').toString();
        if (!cat && !name) return 'wholesale';
        return canonicalizeVendorCategory(cat).key;
    }, [canonicalizeVendorCategory]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // The leakage tile's own state. Null until the months list answers, so the
    // tile appears with real numbers rather than flashing a placeholder.
    const [leakageTile, setLeakageTile] = useState(null);

    useEffect(() => {
        let alive = true;
        leakageApi.months()
            .then((months) => {
                if (!alive || !Array.isArray(months) || months.length === 0) return;
                const completed = months.find((m) => m.state === 'COMPLETED');
                if (completed) {
                    return leakageApi.month(completed.month).then((report) => {
                        if (!alive || report.state !== 'COMPLETED') return;
                        const share = report.headline.share_of_food_cost;
                        setLeakageTile({
                            month: completed.month,
                            subtitle: `${monthLabel(completed.month)} • ${inr(report.headline.total_lost_cost)} lost`
                                + (share !== null && share !== undefined ? ` (${pct(share)} of food cost)` : ''),
                            action: 'View Analytics',
                        });
                    });
                }
                // No calculated month yet. The banner still has something to
                // say whenever a month is closed and waiting: READY is the
                // common case, but RECALL_PENDING (the wastage question was
                // opened and left) and FAILED (a run stopped) are months that
                // are equally one tap from a report. Matching only READY hid
                // the banner entirely for exactly the owners who had started
                // and not finished — the ones who most needed the way back in.
                const pending = months.find((m) => m.state === 'READY'
                    || m.state === 'RECALL_PENDING'
                    || m.state === 'FAILED'
                    || m.state === 'RUNNING');
                if (pending) {
                    const running = pending.state === 'RUNNING';
                    setLeakageTile({
                        month: pending.month,
                        subtitle: running
                            ? `${monthLabel(pending.month)} is being calculated`
                            : pending.state === 'FAILED'
                                ? `${monthLabel(pending.month)} stopped part-way`
                                : `${monthLabel(pending.month)} is closed and ready to calculate`,
                        action: running ? 'See progress' : pending.state === 'FAILED' ? 'Try again' : 'Run it',
                    });
                }
                return undefined;
            })
            // A tile is a convenience. If leakage is disabled for this
            // deployment, or the month list fails, the reorder screen carries on.
            .catch(() => {});
        return () => { alive = false; };
    }, []);
    const [items, setItems] = useState([]); // All low stock items for Order Items section
    const [criticalItems, setCriticalItems] = useState([]); // Only A-category items for Critical section
    const [selected, setSelected] = useState({});
    const [itemUnits, setItemUnits] = useState({}); // Track selected unit for each item
    const [openUnitDropdownId, setOpenUnitDropdownId] = useState(null);
    const [unitDropdownMenuStyle, setUnitDropdownMenuStyle] = useState(null);
    const unitTriggerRefs = useRef({});

    const getResolvedBusinessId = useCallback(() => {
        const fromStorage = Number(localStorage.getItem('businessId'));
        if (fromStorage) return fromStorage;

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const fromUser = Number(user.businessId || user.business_id || user.businessid);
            if (fromUser) {
                localStorage.setItem('businessId', String(fromUser));
                return fromUser;
            }
        } catch {}

        return 0;
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // SINGLE OWNER of the preferred-vendors storage.
    // Every write to `preferredVendors` MUST go through this writer so the list
    // and its business id are always saved together, never one without the other.
    //
    // Non-destructive by default: this writer REFUSES to persist an empty list.
    // A wipe is only ever legitimate for tenant isolation, and that path uses
    // removeItem() directly (see the rehydrate effect) — never this writer.
    // Any doubt (empty input, missing id, thrown error) means "keep what we have".
    // Returns the list it decided to keep so callers can mirror it into state.
    // ─────────────────────────────────────────────────────────────────────────
    const persistPreferredVendors = useCallback((list) => {
        const safeList = Array.isArray(list) ? list : [];

        // Never save an emptied list back to storage. Keep whatever is already there.
        if (safeList.length === 0) {
            return safeList;
        }

        try {
            localStorage.setItem('preferredVendors', JSON.stringify(safeList));
            const bizId = getResolvedBusinessId();
            if (bizId) {
                localStorage.setItem('preferredVendorsBusinessId', String(bizId));
            }
        } catch (e) {
            // On any failure, keep the in-memory list; do not delete storage.
            console.error('   ❌ Failed to persist preferred vendors:', e?.message || e);
        }

        return safeList;
    }, [getResolvedBusinessId]);

    const mapDashboardAlerts = useCallback((dashboardAlertsRes) => {
        const alertList = Array.isArray(dashboardAlertsRes?.data)
            ? dashboardAlertsRes.data
            : Array.isArray(dashboardAlertsRes?.data?.data)
                ? dashboardAlertsRes.data.data
                : [];

        return alertList.map(alert => ({
            item_id: alert.item_id,
            name: alert.name,
            unit: alert.unit,
            current_stock: Number(alert.current_stock || 0),
            reorder_point: Number(alert.reorder_point || 0),
            safety_stock: Number(alert.safety_stock || 0),
            urgency_level: alert.urgency_level
        }));
    }, []);

    const withTimeout = useCallback((promise, ms, fallback = null) => {
        let timer;
        return Promise.race([
            promise,
            new Promise((resolve) => {
                timer = setTimeout(() => resolve(fallback), ms);
            })
        ]).finally(() => clearTimeout(timer));
    }, []);

    const positionUnitDropdownMenu = useCallback((itemId, optionCount = 0) => {
        if (typeof window === 'undefined') return;

        const key = String(itemId);
        const triggerEl = unitTriggerRefs.current[key];
        if (!triggerEl) return;

        const rect = triggerEl.getBoundingClientRect();
        const menuWidth = Math.max(Math.round(rect.width), 64);
        const _estimatedRows = Math.max(1, Number(optionCount) || 4);
        const rawTop = rect.bottom + 4;
        const clampedTop = Math.max(8, Math.round(Math.min(rawTop, window.innerHeight - 56)));
        const maxLeft = window.innerWidth - menuWidth - 8;
        const availableBelow = Math.max(56, Math.floor(window.innerHeight - clampedTop - 8));

        setUnitDropdownMenuStyle({
            top: clampedTop,
            left: Math.max(8, Math.round(Math.min(rect.left, maxLeft))),
            width: menuWidth,
            maxHeight: Math.min(240, availableBelow),
        });
    }, []);
    
    // Debug: Monitor items state changes
    useEffect(() => {
        console.log('📦 ITEMS STATE CHANGED:', {
            count: items.length,
            firstFew: items.slice(0, 3).map(i => i.name)
        });
    }, [items]);
    
    // Debug: Monitor criticalItems state changes
    useEffect(() => {
        console.log('🔥 CRITICAL ITEMS STATE CHANGED:', {
            count: criticalItems.length,
            items: criticalItems.slice(0, 3).map(i => i.name)
        });
    }, [criticalItems]);
    
    // Initialize unit selections when items are loaded
    useEffect(() => {
        if (items.length > 0) {
            const units = {};
            items.forEach(item => {
                if (item.item_id && item.unit) {
                    // Default to the item's base unit, but normalize it
                    const baseUnit = item.unit.toString().toLowerCase().trim();
                    if (baseUnit === 'gm' || baseUnit === 'gram' || baseUnit === 'grams') {
                        units[item.item_id] = 'g';
                    } else if (baseUnit === 'kg' || baseUnit === 'kilogram' || baseUnit === 'kilograms') {
                        units[item.item_id] = 'kg';
                    } else if (baseUnit === 'ml' || baseUnit === 'milliliter' || baseUnit === 'milliliters') {
                        units[item.item_id] = 'ml';
                    } else if (baseUnit === 'ltr' || baseUnit === 'liter' || baseUnit === 'liters') {
                        units[item.item_id] = 'l';
                    } else {
                        units[item.item_id] = item.unit;
                    }
                }
            });
            setItemUnits(units);
        }
    }, [items]);

    // Close custom unit dropdown when clicking outside or pressing Escape
    useEffect(() => {
        const handleOutsideClick = (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
                setOpenUnitDropdownId(null);
                setUnitDropdownMenuStyle(null);
                return;
            }

            const clickedWrapper = target.closest('.cr-unit-dropdown-wrapper');
            const clickedPortalMenu = target.closest('.cr-unit-menu-portal');
            if (!clickedWrapper && !clickedPortalMenu) {
                setOpenUnitDropdownId(null);
                setUnitDropdownMenuStyle(null);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setOpenUnitDropdownId(null);
                setUnitDropdownMenuStyle(null);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // If item list changes and open dropdown row no longer exists, close it
    useEffect(() => {
        setOpenUnitDropdownId((prev) => {
            if (!prev) return prev;
            const exists = items.some((row) => String(row.item_id) === String(prev));
            return exists ? prev : null;
        });
    }, [items]);

    // Keep portal menu anchored to the trigger while scrolling/resizing.
    useEffect(() => {
        if (!openUnitDropdownId) {
            setUnitDropdownMenuStyle(null);
            return;
        }

        const updatePosition = () => positionUnitDropdownMenu(openUnitDropdownId);
        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);

        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [openUnitDropdownId, positionUnitDropdownMenu]);
    
    const [creating, setCreating] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [orderNotes, setOrderNotes] = useState('');
    const [requestedDeliveryDate, setRequestedDeliveryDate] = useState('');
    const [successInfo, setSuccessInfo] = useState(null);
    const [showInlineAdd, setShowInlineAdd] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);
    
    // Search handlers
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };
    
    const clearSearch = () => {
        setSearchTerm('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };
    const [availableItems, setAvailableItems] = useState([]);
    const [preferredVendors, setPreferredVendors] = useState([]);

    // **CLEANUP: Clear genuinely-corrupted localStorage on mount**
    // Non-destructive by default (Part A, principle 2): the ONLY thing we remove
    // is data whose serialized form is provably corrupt (a leaked "Proxy" string).
    // A generic read error is "doubt", not proof of corruption, so we keep the
    // data and only log — deleting the vendor list on a transient hiccup is the
    // exact "comes and goes" bug this guards against.
    useEffect(() => {
        const keysToCheck = ['pendingVendorSelection', 'preferredVendors', 'reorderDraftBills'];
        keysToCheck.forEach(key => {
            try {
                const raw = localStorage.getItem(key);
                if (raw && raw.includes('Proxy')) {
                    console.warn(`🧹 Clearing corrupted ${key} from localStorage`);
                    localStorage.removeItem(key);
                }
            } catch (e) {
                // On any doubt, keep what we have — never delete.
                console.error(`🔴 Error checking ${key} (keeping it):`, e.message);
            }
        });
    }, []);

    // Convert full unit names to short forms
    const getShortUnit = useCallback((unit) => {
        if (!unit) return '';
        const u = unit.toString().trim().toLowerCase();
        
        // Map full names to short forms
        const unitMap = {
            'kilogram': 'kg',
            'kilograms': 'kg',
            'gram': 'g',
            'grams': 'g',
            'liter': 'L',
            'liters': 'L',
            'ltr': 'L',
            'milliliter': 'ml',
            'milliliters': 'ml',
            'piece': 'pc',
            'pieces': 'pc',
            'bottle': 'btl',
            'bottles': 'btl',
            'packet': 'pkt',
            'packets': 'pkt',
            'box': 'box',
            'boxes': 'box',
            'pack': 'pk',
            'packs': 'pk',
            'sprig': 'sprig',
            'sprigs': 'sprigs',
            'large': 'lg',
            'medium': 'med',
            'small': 'sm',
            'dozen': 'doz',
            'can': 'can',
            'cans': 'cans',
            'jar': 'jar',
            'jars': 'jars',
            'bag': 'bag',
            'bags': 'bags',
            'bundle': 'bdl',
            'bundles': 'bdl'
        };
        
        return unitMap[u] || unit; // Return short form or original if not in map
    }, []);

    // Smart quantity formatter - removes unnecessary decimals and auto-converts units
    // Examples: 1.0 kg → 1 kg, 0.1 kg → 100 g, 0.036 kg → 36 g, 1.5 kg → 1.5 kg
    const formatStockDisplay = useCallback((value, unit) => {
        const v = Number(value || 0);
        const u = (unit || '').toString().trim();
        
        if (!u) return `${v}`;
        
        const lc = u.toLowerCase();
        const isKg = lc === 'kg' || lc === 'kilogram' || lc === 'kilograms';
        const isL = lc === 'l' || lc === 'liter' || lc === 'liters' || lc === 'ltr';
        const isG = lc === 'g' || lc === 'gram' || lc === 'grams';
        const isML = lc === 'ml' || lc === 'milliliter' || lc === 'milliliters';
        
        // Helper to format number without unnecessary decimals
        const formatNum = (num) => {
            if (Number.isInteger(num)) return num.toString();
            // Remove trailing zeros: 1.50 → 1.5, 1.00 → 1
            return num.toFixed(2).replace(/\.?0+$/, '');
        };
        
        // Auto-convert kg to g if magnitude in grams would be less than 1000
        if (isKg) {
            const grams = v * 1000;
            if (Math.abs(grams) < 1000) {
                return `${formatNum(grams)} g`;
            }
            return `${formatNum(v)} kg`;
        }

        // Auto-convert L to ml if magnitude in ml would be less than 1000
        if (isL) {
            const ml = v * 1000;
            if (Math.abs(ml) < 1000) {
                return `${formatNum(ml)} ml`;
            }
            return `${formatNum(v)} L`;
        }

        // For grams: convert to kg if magnitude >= 1000
        if (isG) {
            if (Math.abs(v) >= 1000) {
                const kg = v / 1000;
                return `${formatNum(kg)} kg`;
            }
            return `${formatNum(v)} g`;
        }

        // For ml: convert to L if magnitude >= 1000
        if (isML) {
            if (Math.abs(v) >= 1000) {
                const liters = v / 1000;
                return `${formatNum(liters)} L`;
            }
            return `${formatNum(v)} ml`;
        }
        
        // Default: format number with unit
        return `${formatNum(v)} ${u}`;
    }, []);

    // Human-friendly quantity formatter (matches screenshots)
    // - If quantity < 1000, show in g/ml
    // - If quantity >= 1000, show in kg/L
    // Canonical, app-wide display: full names (Gram/Kilogram/Millilitre/Litre/Pcs), auto-scaled.
    const formatQtyDisplay = useCallback((value, unit) => {
        const parts = formatQuantityParts(Number(value || 0), unit);
        return { value: parts.qty, unit: parts.unit };
    }, []);

    const formatQtyWithWords = useCallback((value, unit) => {
        const parts = formatQuantityParts(Number(value || 0), unit);
        return { value: parts.qty, unit: parts.unit };
    }, []);

    // Rehydrate preferred vendors from localStorage (if any).
    // Rules (non-destructive by default):
    //   • Belongs to current business  → use it.
    //   • Has no business id (old data) → MIGRATE: stamp with current id, keep it.
    //   • Belongs to a different business → clear (the ONE legitimate wipe).
    //   • Any doubt / thrown error       → keep what we have, never delete.
    useEffect(() => {
        try {
            const currentBusinessId = getResolvedBusinessId();
            const raw = localStorage.getItem('preferredVendors');
            const storedBusinessId = localStorage.getItem('preferredVendorsBusinessId');

            if (!raw) return;

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed) || parsed.length === 0) return;

            // MIGRATE legacy data (no business id stamped) — never delete it.
            if (!storedBusinessId) {
                console.warn('   ⚠️ Legacy vendor data without business id - migrating (stamping current id)');
                if (currentBusinessId) {
                    localStorage.setItem('preferredVendorsBusinessId', String(currentBusinessId));
                }
                setPreferredVendors(parsed);
                return;
            }

            // Correct tenant isolation: data belongs to a different business → clear.
            if (currentBusinessId && Number(storedBusinessId) !== currentBusinessId) {
                console.warn('   ⚠️ Business ID mismatch! Clearing stale vendor data (tenant isolation)');
                localStorage.removeItem('preferredVendors');
                localStorage.removeItem('preferredVendorsBusinessId');
                setPreferredVendors([]);
                return;
            }

            // Belongs to current business → use it.
            setPreferredVendors(parsed);
        } catch (e) {
            // On any doubt, keep what we have — do not delete.
            console.error('   ❌ Error loading vendors from localStorage (keeping current):', e);
        }
    }, [getResolvedBusinessId]);

    // Refresh vendor order counts from API when vendors are loaded
    const [vendorCountsRefreshed, setVendorCountsRefreshed] = useState(false);
    
    useEffect(() => {
        const refreshVendorCounts = async () => {
            // Skip if no vendors, already refreshed, or currently loading
            if (preferredVendors.length === 0 || vendorCountsRefreshed || loading) {
                return;
            }
            
            try {
               
                const businessId = Number(localStorage.getItem('businessId'));
                if (!businessId) {
                    console.warn('   ⚠️ No businessId found');
                    return;
                }
                
                const fallbackLanguage = language || localStorage.getItem('selectedLanguage') || 'en';
                const response = await http.get(`/vendor-management/vendors?language=${fallbackLanguage}`);

                // The validation list only counts if the call SUCCEEDED and returned
                // at least one vendor. Empty or failed → skip pruning entirely, keep
                // everything. This is what stops an empty/failed response from wiping
                // the list (the root cause of vendors "coming and going").
                const canPrune = response?.success && Array.isArray(response.data) && response.data.length > 0;

                if (canPrune) {
                    // Compare vendor ids as NUMBERS to avoid text-vs-number false mismatches.
                    const activeVendorIds = new Set(response.data.map(v => Number(v.vendor_id)));

                    let updateCount = 0;
                    let deletedCount = 0;
                    const updatedVendors = preferredVendors.filter(prefVendor => {
                        // User choices are sacred — never prune a user-added vendor.
                        if (prefVendor.userAdded) return true;

                        // Only an auto-seeded vendor the authoritative list explicitly
                        // lacks may be removed.
                        if (!activeVendorIds.has(Number(prefVendor.vendor_id))) {
                            console.warn(`   🗑️ Auto-seeded vendor ${prefVendor.name} (ID: ${prefVendor.vendor_id}) no longer exists - pruning`);
                            deletedCount++;
                            return false;
                        }
                        return true;
                    }).map(prefVendor => {
                        const freshVendor = response.data.find(v => Number(v.vendor_id) === Number(prefVendor.vendor_id));
                        if (freshVendor) {
                            const newCount = freshVendor.total_purchase_orders || 0;
                            updateCount++;
                            return {
                                ...prefVendor,
                                total_purchase_orders: newCount,
                                average_rating: freshVendor.average_rating || prefVendor.average_rating,
                                total_orders: freshVendor.total_orders || prefVendor.total_orders
                            };
                        }
                        return prefVendor;
                    });

                    // If pruning would empty the whole list, refuse — keep current vendors.
                    if (updatedVendors.length === 0) {
                        console.warn('   ⚠️ Pruning would empty the vendor list - refusing, keeping current vendors');
                        setVendorCountsRefreshed(true);
                        return;
                    }

                    if (updateCount > 0 || deletedCount > 0) {
                        setPreferredVendors(updatedVendors);
                        // Save through the single writer (list + business id together).
                        persistPreferredVendors(updatedVendors);
                        setVendorCountsRefreshed(true);
                    } else {
                        console.warn('   ⚠️ No vendors were updated');
                    }
                } else {
                    // Empty or failed response → skip pruning, keep everything.
                    console.warn('   ⚠️ Vendor validation list empty/failed - skipping prune, keeping all vendors');
                }
            } catch (error) {
                console.error('   ❌ Error refreshing vendor counts:', error.message);
            }
        };
        
        // Small delay to ensure vendors are fully loaded
        const timer = setTimeout(refreshVendorCounts, 500);
        return () => clearTimeout(timer);
    }, [preferredVendors.length, vendorCountsRefreshed, loading, language, persistPreferredVendors]); // Trigger when vendors are loaded

    // Debug: Monitor preferredVendors state changes
    useEffect(() => {
        if (preferredVendors.length > 0) {
            preferredVendors.forEach((v, idx) => {
                console.log(`   ${idx + 1}. ${v.name} [${v.normalizedCategoryLabel || v.category}] - ${v.total_purchase_orders || 0} orders`);
            });
        } else {
            console.warn('⚠️⚠️⚠️ PREFERRED VENDORS ARRAY IS EMPTY! ⚠️⚠️⚠️');
        }
    }, [preferredVendors]);

    // Safety check: Restore items if they get cleared accidentally
    useEffect(() => {
        if (items.length === 0 && !loading) {
            try {
                const backup = localStorage.getItem('reorderItemsBackup');
                if (backup) {
                    const restoredItems = JSON.parse(backup);
                    if (Array.isArray(restoredItems) && restoredItems.length > 0) {
                        setItems(restoredItems);
                    }
                }
            } catch (e) {
                console.warn('Could not restore items from backup:', e);
            }
        }
    }, [items.length, loading]);

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                // Onboarding-based behavior: first 7 days show ALL items
                const now = Date.now();
                let firstAt = 0;
                try {
                    const saved = localStorage.getItem('reorderOnboardingAt');
                    if (saved) firstAt = parseInt(saved, 10) || 0;
                } catch {}
                if (!firstAt) {
                    firstAt = now;
                    try { localStorage.setItem('reorderOnboardingAt', String(firstAt)); } catch {}
                }
                const daysSince = Math.floor((now - firstAt) / (24 * 60 * 60 * 1000));
                let inFirst7Days = daysSince <= 7;
                const after14Days = daysSince >= 14;
                // Optional dev override via URL or localStorage: ?showAll=1
                try {
                    const url = new URL(window.location.href);
                    const showAllParam = url.searchParams.get('showAll');
                    const lsOverride = localStorage.getItem('reorderShowAllOverride');
                    if (showAllParam === '1' || lsOverride === '1') inFirst7Days = true;
                } catch {}
                
                
                                // Use the smart categorization endpoint for perfect vendor assignments
                const bId = getResolvedBusinessId();
                const currentLanguage = language || localStorage.getItem('selectedLanguage') || 'en';

                                // Fetch dashboard alerts independently so Critical section can render fast.
                                const dashboardAlertsPromise = MinimalStockService.getDashboardAlerts(bId, currentLanguage)
                                    .catch(() => null)
                                    .then((res) => {
                                        const mapped = mapDashboardAlerts(res);
                                        if (mapped.length > 0) {
                                            setCriticalItems(mapped);
                                        }
                                        return mapped;
                                    });

                                // XYZ analysis can be slow in large datasets; keep it non-blocking for first paint.
                                const xyzPromise = withTimeout(
                                    http.get(`/xyz-analysis/calculate?businessId=${bId}`).catch(() => null),
                                    1500,
                                    null
                                );
                
                                const [categoryAssignmentsRes, overviewRes, allItemsRes, xyzAnalysisRes, dashboardAlignedCriticalItems] = await Promise.all([
                    http.get(`/inventory/items/${bId}/category-assignments?includeAll=true&language=${currentLanguage}`),
                    http.get(`/stock-in/v2/inventory/overview?businessId=${bId}&language=${currentLanguage}`).catch(() => null),
                    http.get(`/inventory/items/${bId}?language=${currentLanguage}`).catch(() => null),
                                    xyzPromise,
                                    dashboardAlertsPromise
                ]);

                // Show UI immediately even with empty data
                setLoading(false);

                                // Validate response shape: backend returns { success, data: { assignments: [ { vendor, vendorCategory, items, itemCount } ] } }
                                if (!categoryAssignmentsRes?.success || !Array.isArray(categoryAssignmentsRes?.data?.assignments)) {
                                        console.warn('⚠️ Smart categorization missing/invalid, falling back to old system');
                                        throw new Error(categoryAssignmentsRes?.error || 'Invalid category-assignments response');
                                }

                                const assignments = categoryAssignmentsRes.data.assignments;
                            
                                const overviewListRaw = (overviewRes?.success ? (overviewRes.data || []) : []);
                                // Build quick lookups
                                const categoryByItemId = new Map(
                                    (allItemsRes?.success ? (allItemsRes.data || []) : [])
                                        .map(row => [row.item_id, row.category])
                                );
                                const overviewMap = overviewListRaw
                                    .reduce((acc, it) => {
                                        acc[it.item_id] = {
                                            unit_cost: Number(it.unit_cost || 0),
                                            unit: it.unit || it.unit_symbol || it.unit_name || '',
                                            quantity: Number(it.quantity || 0),
                                            name: it.item_name,
                                            is_newly_added: !!it.is_newly_added
                                        };
                                        return acc;
                                    }, {});

                // Build XYZ category lookup from XYZ analysis results (A, B, C classification)
                const xyzCategoryMap = new Map();
                if (xyzAnalysisRes?.success && xyzAnalysisRes?.data) {
                    // XYZ Analysis returns data.items array with abc_category field
                    const items = xyzAnalysisRes.data.items || [];
                    items.forEach(item => {
                        xyzCategoryMap.set(item.item_id, item.abc_category); // FIXED: API returns abc_category, not xyz_category
                    });
                
                } else {
                    console.warn('⚠️ XYZ Analysis data not available or invalid structure');
                }                                // Build vendors and low stock items from assignments
                                const allVendors = [];
                                const allEnhancedItems = [];

                                assignments.forEach(a => {
                                    const vendor = a.vendor;
                                    const categoryKey = a.vendorCategory || vendor?.vendor_category || vendor?.category || 'wholesale';
                                    const items = Array.isArray(a.items) ? a.items : [];

                                    // collect vendor once
                                    if (vendor && !allVendors.find(v => v.vendor_id === vendor.vendor_id)) {
                                        allVendors.push({
                                            ...vendor,
                                            category: vendor.vendor_category || categoryKey,
                                            vendor_category: vendor.vendor_category || categoryKey
                                        });
                                    }

                                    // enhance items
                                    items.forEach(item => {
                                        const ov = overviewMap[item.item_id] || {};
                                        const liveQty = Number(ov.quantity ?? item.current_stock ?? 0);
                                        const unitLabel = item.unit || ov.unit || '';
                                        const rp = Number(item.reorder_point || 0);
                                        const ss = Number(item.safety_stock || 0);
                                        const isLow = (rp > 0 && liveQty <= rp) || (ss > 0 && liveQty <= ss);
                                        const enhancedBase = {
                                            ...item,
                                            item_id: item.item_id,
                                            name: item.name,
                                            unit: unitLabel,
                                            current_stock: liveQty,
                                            reorder_point: rp,
                                            safety_stock: ss,
                                            manual_abc_category: xyzCategoryMap.get(item.item_id) || 'C', // Use XYZ analysis result
                                            smart_vendor_category: categoryKey,
                                            smart_vendor: vendor,
                                            is_low_stock: isLow
                                        };
                                        const enhancedItem = {
                                            ...enhancedBase,
                                            reorder_qty: computeSuggestedQty(enhancedBase)
                                        };
                                        allEnhancedItems.push(enhancedItem);
                                    });
                                });

                                if (allEnhancedItems.length === 0) {
                                    console.warn('⚠️ Smart categorization returned no items; falling back');
                                    throw new Error('No categorized items');
                                }
                
                // Derive low-stock list and default ordering
                const onlyLow = allEnhancedItems.filter(x => x.is_low_stock === true);
                const sortByUrgency = (a, b) => {
                    const baseA = (a.reorder_point || a.safety_stock || 1);
                    const baseB = (b.reorder_point || b.safety_stock || 1);
                    const ratioA = baseA > 0 ? (a.current_stock / baseA) : 1;
                    const ratioB = baseB > 0 ? (b.current_stock / baseB) : 1;
                    if (ratioA !== ratioB) return ratioA - ratioB;
                    if (a.is_newly_added !== b.is_newly_added) return a.is_newly_added ? 1 : -1;
                    return a.name.localeCompare(b.name);
                };
                const sortedLow = onlyLow.sort(sortByUrgency);

                // First 7 days: show ALL items from overview
                let itemsForOrder = sortedLow;
                if (inFirst7Days) {
                    itemsForOrder = overviewListRaw.map(it => {
                        const baseItem = {
                            item_id: it.item_id,
                            name: it.item_name || it.name,
                            unit: it.unit || it.unit_symbol || it.unit_name || '',
                            current_stock: Number(it.quantity || 0),
                            reorder_point: Number(it.reorder_point || it.minimum_stock_level || 0),
                            safety_stock: Number(it.safety_stock || it.maximum_stock_level || 0),
                            smart_vendor_category: (() => {
                                const fromSmart = assignments.find(a => (a.items || []).some(x => x.item_id === it.item_id))?.vendorCategory;
                                if (fromSmart) return canonicalizeVendorCategory(fromSmart).key;
                                const cat = categoryByItemId.get(it.item_id) || '';
                                return deriveVendorCategoryKey(cat, it.item_name || it.name || '');
                            })(),
                            is_low_stock: false,
                            is_newly_added: !!it.is_newly_added
                        };
                        return {
                            ...baseItem,
                            reorder_qty: computeSuggestedQty(baseItem)
                        };
                    }).sort((a, b) => {
                        if (a.is_newly_added !== b.is_newly_added) return a.is_newly_added ? 1 : -1;
                        return a.name.localeCompare(b.name);
                    });
                 } else if (sortedLow.length === 0 && overviewListRaw.length > 0) {
                    // Fallback: If no low stock items but inventory exists, show all items
                    // This happens when reorder points are not set yet
                    itemsForOrder = overviewListRaw.map(it => {
                        const baseItem = {
                            item_id: it.item_id,
                            name: it.item_name || it.name,
                            unit: it.unit || it.unit_symbol || it.unit_name || '',
                            current_stock: Number(it.quantity || 0),
                            reorder_point: Number(it.reorder_point || it.minimum_stock_level || 0),
                            safety_stock: Number(it.safety_stock || it.maximum_stock_level || 0),
                            smart_vendor_category: (() => {
                                const fromSmart = assignments.find(a => (a.items || []).some(x => x.item_id === it.item_id))?.vendorCategory;
                                if (fromSmart) return canonicalizeVendorCategory(fromSmart).key;
                                const cat = categoryByItemId.get(it.item_id) || '';
                                return deriveVendorCategoryKey(cat, it.item_name || it.name || '');
                            })(),
                            is_low_stock: false,
                            is_newly_added: !!it.is_newly_added
                        };
                        return {
                            ...baseItem,
                            reorder_qty: computeSuggestedQty(baseItem)
                        };
                    }).sort((a, b) => {
                        if (a.is_newly_added !== b.is_newly_added) return a.is_newly_added ? 1 : -1;
                        return a.name.localeCompare(b.name);
                    });
                } else if (after14Days) {
                    // 14+ days: use learned consumption-based suggestions
                    try {
                        const token = localStorage.getItem('token');
                        if (!bId || Number.isNaN(bId)) {
                            return;
                        }

                        const suggestedUrl = `/api/reorder/suggested-items/${bId}?language=${currentLanguage}`;
                        const suggestedResponse = await fetch(suggestedUrl, {
                            headers: { 'Authorization': `Bearer ${token}` }
                        });

                        // Treat 404 as "feature not available" and fall back silently
                        if (suggestedResponse.status === 404) {
                            return;
                        }

                        if (!suggestedResponse.ok) {
                            throw new Error(`HTTP ${suggestedResponse.status}: Failed to fetch suggested items`);
                        }

                        const suggestedRes = await suggestedResponse.json();
                        if (suggestedRes?.success && Array.isArray(suggestedRes.suggestedItems) && suggestedRes.suggestedItems.length > 0) {
                            const suggested = suggestedRes.suggestedItems;
                            // Build a quick lookup for category from smart assignments
                            const assignmentCategoryByItem = new Map();
                            assignments.forEach(a => {
                                (a.items || []).forEach(x => assignmentCategoryByItem.set(x.item_id, a.vendorCategory || 'wholesale'));
                            });
                            // Map suggestions to UI items
                            itemsForOrder = suggested.map(s => {
                                const ov = overviewMap[s.itemId] || {};
                                const baseItem = {
                                    item_id: s.itemId,
                                    name: s.itemName,
                                    unit: ov.unit || '',
                                    current_stock: Number(ov.quantity ?? 0),
                                    reorder_point: Number(s.reorderPoint ?? 0),
                                    safety_stock: Number(s.safetyStock ?? 0),
                                    smart_vendor_category: (() => {
                                        const fromSmart = assignmentCategoryByItem.get(s.itemId);
                                        if (fromSmart) return canonicalizeVendorCategory(fromSmart).key;
                                        const cat = (allItemsRes?.success ? (allItemsRes.data || []) : []).find(r => r.item_id === s.itemId)?.category || '';
                                        return deriveVendorCategoryKey(cat, s.itemName || '');
                                    })(),
                                    is_low_stock: true,
                                    is_newly_added: !!ov.is_newly_added,
                                    urgency_score: Number(s.urgencyScore || 999)
                                };
                                return {
                                    ...baseItem,
                                    reorder_qty: computeSuggestedQty(baseItem, s.recommendedOrderQty)
                                };
                            })
                            // Newly added last, then by urgency, then by name
                            .sort((a, b) => {
                                if (a.is_newly_added !== b.is_newly_added) return a.is_newly_added ? 1 : -1;
                                if (a.urgency_score !== b.urgency_score) return a.urgency_score - b.urgency_score;
                                return a.name.localeCompare(b.name);
                            });
                        }
                    } catch (e) {
                        console.warn('⚠️ Suggested-items fetch failed, using low-stock list:', e.message);
                    }
                }
                
                // Keep reorder card consistent with dashboard thresholds: critical by safety, low by reorder point.
                const criticalItems = sortedLow
                    .slice()
                    .sort((a, b) => {
                        const severityRank = (item) => {
                            const stock = Number(item.current_stock || 0);
                            const safety = Number(item.safety_stock || 0);
                            const reorder = Number(item.reorder_point || 0);
                            if (safety > 0 && stock <= safety) return 1;
                            if (reorder > 0 && stock <= reorder) return 2;
                            return 3;
                        };

                        const rankA = severityRank(a);
                        const rankB = severityRank(b);
                        if (rankA !== rankB) return rankA - rankB;

                        const baseA = Number(a.reorder_point || a.safety_stock || 1);
                        const baseB = Number(b.reorder_point || b.safety_stock || 1);
                        const ratioA = baseA > 0 ? (Number(a.current_stock || 0) / baseA) : 1;
                        const ratioB = baseB > 0 ? (Number(b.current_stock || 0) / baseB) : 1;
                        if (ratioA !== ratioB) return ratioA - ratioB;

                        return a.name.localeCompare(b.name);
                    });
                
                // loaded items and categories from smart system
                
                setItems(itemsForOrder); // First 7 days = ALL; else low stock
                // Save to localStorage as backup
                try {
                    localStorage.setItem('reorderItemsBackup', JSON.stringify(itemsForOrder));
                } catch (e) {
                    console.warn('Could not backup items:', e);
                }
                if (dashboardAlignedCriticalItems.length === 0) {
                    setCriticalItems(criticalItems);
                }
                setVendors(allVendors);
                setAvailableItems(allItemsRes?.success ? (allItemsRes.data || []) : []);
                // Seed one vendor per category (first found) — but ONLY when the live
                // in-memory list is genuinely empty at this instant, so no timing race
                // against rehydrate can give a stale answer or clobber existing vendors.
                try {
                    const seeded = dedupeByCategory((allVendors || []).map(v => withNormalizedCategory(v)));
                    if (seeded.length > 0) {
                        setPreferredVendors(prev => {
                            const live = Array.isArray(prev) ? prev : [];
                            if (live.length > 0) return live; // already have vendors → leave untouched
                            return persistPreferredVendors(seeded);
                        });
                    }
                } catch (e) {
                    console.error('   ❌ Error seeding vendors:', e);
                }
                
                // Preselect items with positive suggested qty (will be none in first-7-days by default)
                const sel = {};
                itemsForOrder.forEach(x => { if ((x.reorder_qty || 0) > 0) sel[x.item_id] = true; });
                setSelected(sel);
                
                // Set default delivery date (7 days from now)
                const defaultDate = new Date();
                defaultDate.setDate(defaultDate.getDate() + 7);
                setRequestedDeliveryDate(defaultDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }));
                
            } catch (e) {
                console.warn('⚠️ Smart categorization failed, falling back to old system:', e.message);
                
                try {
                    // Fallback to old system
                        const fbId = getResolvedBusinessId();
                        const fallbackLanguage = language || localStorage.getItem('selectedLanguage') || 'en';
                        
                        const [itemsRes, vendorsRes, overviewRes, xyzAnalysisRes, dashboardAlertsRes] = await Promise.all([
                            MinimalStockService.getCriticalItems(fbId),
                            http.get(`/vendor-management/vendors?language=${fallbackLanguage}`),
                            http.get(`/stock-in/v2/inventory/overview?businessId=${fbId}&language=${fallbackLanguage}`).catch(() => null),
                            http.get(`/xyz-analysis/calculate?businessId=${fbId}`).catch(() => null),
                            MinimalStockService.getDashboardAlerts(fbId, fallbackLanguage).catch(() => null)
                        ]);
                    
                    if (!itemsRes?.success) throw new Error(itemsRes?.error || 'Failed to load items');
                    if (!vendorsRes?.success) throw new Error(vendorsRes?.error || 'Failed to load vendors');
                    
                    // Create XYZ category mapping for fallback (using abc_category from XYZ Analysis)
                    const xyzCategoryMapFallback = {};
                    if (xyzAnalysisRes?.data?.items) {
                        xyzAnalysisRes.data.items.forEach(item => {
                            if (item.item_id && item.abc_category) {
                                xyzCategoryMapFallback[item.item_id] = item.abc_category; // FIXED: API returns abc_category
                            }
                        });
                    }
                    
                    // Use old system data processing
                    const overviewMapFB = (overviewRes?.success ? (overviewRes.data || []) : [])
                        .reduce((acc, it) => {
                            acc[it.item_id] = {
                                unit: it.unit || it.unit_symbol || it.unit_name || '',
                                quantity: Number(it.quantity || 0)
                            };
                            return acc;
                        }, {});

                    const enriched = (itemsRes.data || []).map(x => {
                        const ov = overviewMapFB[x.item_id] || {};
                        const liveQty = Number(ov.quantity ?? x.current_stock ?? 0);
                        const rp = Number(x.reorder_point || 0);
                        const ss = Number(x.safety_stock || 0);
                        const base = {
                            ...x,
                            unit: x.unit || ov.unit || '',
                            current_stock: liveQty,
                            reorder_point: rp,
                            safety_stock: ss,
                            abc_category: xyzCategoryMapFallback[x.item_id] || 'C'
                        };
                        return {
                            ...base,
                            reorder_qty: computeSuggestedQty(base),
                            is_low_stock: (rp > 0 && liveQty <= rp) || (ss > 0 && liveQty <= ss)
                        };
                    });

                    // Default view: only low stock, sorted by urgency
                    const lowOnly = enriched.filter(i => i.is_low_stock);
                    const sorted = lowOnly.sort((a, b) => {
                        const baseA = (a.reorder_point || a.safety_stock || 1);
                        const baseB = (b.reorder_point || b.safety_stock || 1);
                        const ratioA = baseA > 0 ? (a.current_stock / baseA) : 1;
                        const ratioB = baseB > 0 ? (b.current_stock / baseB) : 1;
                        if (ratioA !== ratioB) return ratioA - ratioB;
                        if (a.is_newly_added !== b.is_newly_added) return a.is_newly_added ? 1 : -1;
                        return a.name.localeCompare(b.name);
                    });
                    
                    const criticalAItems = [...sorted];
                    
                    const dashboardAlignedCriticalItems = mapDashboardAlerts(dashboardAlertsRes);

                    setItems(sorted);
                    setCriticalItems(dashboardAlignedCriticalItems.length > 0 ? dashboardAlignedCriticalItems : criticalAItems);
                    setVendors(vendorsRes.data || []);
                    
                    // Fallback seed: build "one vendor per category" (same shape as the
                    // smart path) and apply it ONLY if the live list is still empty at
                    // this instant. Never save an empty list.
                    try {
                        const allVendors = vendorsRes.data || [];
                        const initialPreferred = dedupeByCategory(
                            allVendors.map(v => withNormalizedCategory(v))
                        );
                        if (initialPreferred.length > 0) {
                            setPreferredVendors(prev => {
                                const live = Array.isArray(prev) ? prev : [];
                                if (live.length > 0) return live; // already have vendors → leave untouched
                                return persistPreferredVendors(initialPreferred);
                            });
                        }
                    } catch (e) {
                        console.error('   ❌ [Fallback] Error seeding vendors:', e);
                    }
                    
                    // Preselect items
                    const sel = {};
                    // Preselect from the final sorted low-stock list
                    sorted.forEach(x => { if ((x.reorder_qty || 0) > 0) sel[x.item_id] = true; });
                    setSelected(sel);
                    
                    
                } catch (fallbackError) {
                    console.error('❌ Both smart categorization and fallback failed:', fallbackError);
                    setError(fallbackError.message);
                }
            }
        };
        load();
    }, [getResolvedBusinessId, language, mapDashboardAlerts, withTimeout, dedupeByCategory, withNormalizedCategory, deriveVendorCategoryKey, canonicalizeVendorCategory, persistPreferredVendors]);

    const addVendorDirectly = useCallback((vendor, categoryOverride = null) => {
        // Ignore malformed input instead of throwing → "Add" can never fail.
        if (!vendor || typeof vendor !== 'object' || Array.isArray(vendor)) {
            console.warn('   ⚠️ addVendorDirectly ignored malformed vendor input');
            return;
        }

        // Tag as a user choice — this is what makes it immune to pruning.
        const enriched = { ...withNormalizedCategory(vendor, categoryOverride), userAdded: true };

        setPreferredVendors(prev => {
            const list = Array.isArray(prev) ? prev : [];

            // Replace any existing vendor from the same normalized category, then dedupe.
            const filtered = list.filter(v => (v.normalizedCategoryKey || '') !== enriched.normalizedCategoryKey);
            const deduped = dedupeByCategory([...filtered, enriched]);

            // Save through the single writer (list + business id together).
            return persistPreferredVendors(deduped);
        });
    }, [withNormalizedCategory, dedupeByCategory, persistPreferredVendors]);

    // Backup check for vendor selection from localStorage
    // This runs on mount, after 500ms, and whenever window gains focus
    useEffect(() => {
        const checkPendingVendor = () => {
            const pendingVendor = localStorage.getItem('pendingVendorSelection');
            if (pendingVendor) {
                try {
                    const vendor = JSON.parse(pendingVendor);
                    addVendorDirectly(vendor);
                    localStorage.removeItem('pendingVendorSelection');
                } catch (e) {
                    console.error('🔴 Error parsing pendingVendorSelection:', e.message);
                    localStorage.removeItem('pendingVendorSelection');
                }
            }
        };
        
        // Check immediately on mount
        checkPendingVendor();
        
        // Check after short delay (for race conditions)
        const timer1 = setTimeout(checkPendingVendor, 300);
        const timer2 = setTimeout(checkPendingVendor, 1000);
        
        // Check whenever window/document gains focus (user returns to tab/screen)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkPendingVendor();
            }
        };
        
        const handleFocus = () => {
            checkPendingVendor();
        };
        
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        
        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
        };
    }, [addVendorDirectly]); // ensure stable callback is used

    // Handle selected vendor from vendor management
    useEffect(() => {
        
        // Check URL parameters for vendor selection as backup
        const urlParams = new URLSearchParams(window.location.search);
        const vendorFromUrl = urlParams.get('selectedVendor');
        
        if (vendorFromUrl) {
            try {
                const parsedVendor = JSON.parse(decodeURIComponent(vendorFromUrl));
                addVendorDirectly(parsedVendor);
                // Clean up URL
                window.history.replaceState({}, '', window.location.pathname);
            } catch (e) {
                console.error('🔴 Error parsing vendor from URL:', e.message);
                // Clear the URL parameter
                window.history.replaceState({}, '', window.location.pathname);
            }
        }
        
        if (selectedVendor) {
            
            addVendorDirectly(selectedVendor);
            
            // Clear the selected vendor after adding
            if (onClearSelectedVendor) {
                onClearSelectedVendor();
            }
        }
    }, [selectedVendor, onClearSelectedVendor, addVendorDirectly]); // include stable callback

    const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
    const updateQty = (id, qty) => setItems(list => list.map(x => x.item_id === id ? { ...x, reorder_qty: qty } : x));

    // Get available unit options based on the item's base unit
    // Returns array of options for convertible units, or null for non-convertible units
    const getUnitOptions = (baseUnit) => {
        if (!baseUnit) return null;
        
        const unit = baseUnit.toString().toLowerCase().trim();
        
        // Weight conversions: g <-> kg
        if (unit === 'g' || unit === 'gm' || unit === 'gram' || unit === 'grams') {
            return [
                { value: 'g', label: 'g' },
                { value: 'kg', label: 'kg' }
            ];
        }
        if (unit === 'kg' || unit === 'kilogram' || unit === 'kilograms') {
            return [
                { value: 'kg', label: 'kg' },
                { value: 'g', label: 'g' }
            ];
        }
        
        // Volume conversions: ml <-> l
        if (unit === 'ml' || unit === 'milliliter' || unit === 'milliliters') {
            return [
                { value: 'ml', label: 'ml' },
                { value: 'l', label: 'L' }
            ];
        }
        if (unit === 'l' || unit === 'ltr' || unit === 'liter' || unit === 'liters') {
            return [
                { value: 'l', label: 'L' },
                { value: 'ml', label: 'ml' }
            ];
        }
        
        // For other units (pcs, pieces, units, etc.), no conversion available
        return null;
    };

    // Handle unit change for an item and convert quantity
    const handleUnitChange = (itemId, newUnit, currentQty, currentUnit) => {
        // Update the selected unit for this item
        setItemUnits(prev => ({ ...prev, [itemId]: newUnit }));
        
        // If quantity exists, convert it
        if (currentQty && currentQty > 0) {
            let convertedQty = currentQty;
            
            const curr = currentUnit.toLowerCase();
            const next = newUnit.toLowerCase();
            
            // Convert between units
            if (curr === 'kg' && next === 'g') {
                convertedQty = currentQty * 1000;
            } else if (curr === 'g' && next === 'kg') {
                convertedQty = currentQty / 1000;
            } else if (curr === 'l' && next === 'ml') {
                convertedQty = currentQty * 1000;
            } else if (curr === 'ml' && next === 'l') {
                convertedQty = currentQty / 1000;
            }
            
            // Update the quantity
            updateQty(itemId, convertedQty);
        }
    };


    const addItemToOrder = (item) => {
        // Check if item is already in the order
        const exists = items.find(x => x.item_id === item.item_id);
        
            if (exists) {
            // Always scroll to existing item instead of adding (both first 7 days and normal mode)
            const itemElement = document.querySelector(`[data-item-id="${item.item_id}"]`);
            if (itemElement) {
                itemElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center'
                });
                // Highlight the item briefly with smooth animation
                itemElement.classList.add('cr-item-highlight');
                setTimeout(() => {
                    itemElement.classList.remove('cr-item-highlight');
                }, 1000);
            }
            setShowInlineAdd(false);
            setSearchTerm('');
            return;
        }

        // Add the item to the order list (item doesn't exist in order).
        // Manually added items ALSO start at 0 — never re-derive the quantity from
        // stock or reorder points. (See computeSuggestedQty: do not add a formula.)
        const newItem = {
            ...item,
            reorder_qty: 0
        };
        setItems(prev => [...prev, newItem]);
        setSelected(prev => ({ ...prev, [item.item_id]: false }));
        setShowInlineAdd(false);
        setSearchTerm('');
    };

    const filteredAvailableItems = useMemo(() => {
        if (!searchTerm) return [];
        
        // Use smart search for better results - preserves priority ordering (prefix matches first)
        const searchResults = performSmartSearch(availableItems, searchTerm, ['name']);
        
        // Sort within each priority group to maintain search relevance while organizing by newly added status
        const startsWithTerm = searchResults.filter(item => 
            item.name.toLowerCase().startsWith(searchTerm.toLowerCase())
        );
        const containsTerm = searchResults.filter(item => 
            item.name.toLowerCase().includes(searchTerm.toLowerCase()) && 
            !item.name.toLowerCase().startsWith(searchTerm.toLowerCase())
        );
        
        // Sort each group by newly added status, but preserve priority order
        const sortByNewlyAdded = (items) => items.sort((a, b) => {
            if (a.is_newly_added !== b.is_newly_added) {
                return a.is_newly_added ? 1 : -1;
            }
            return 0; // Preserve existing order within same newly_added status
        });
        
        const sortedStartsWith = sortByNewlyAdded([...startsWithTerm]);
        const sortedContains = sortByNewlyAdded([...containsTerm]);
        
        return [...sortedStartsWith, ...sortedContains].slice(0, 10);
    }, [searchTerm, availableItems, items]);

    const selectedItems = useMemo(() => {
        return items.filter(x => selected[x.item_id] && (x.reorder_qty || 0) > 0);
    }, [items, selected]);

    // Create a per-category bill image (PNG) using Canvas API
    const generateBillImage = useCallback((params) => {
        const { categoryLabel, vendor, items, notes, deliveryDate } = params;
        const unitLabelMap = {
            g: 'Gram',
            gram: 'Gram',
            grams: 'Gram',
            kg: 'Kg',
            kilogram: 'Kg',
            kilograms: 'Kg',
            ml: 'ML',
            milliliter: 'ML',
            milliliters: 'ML',
            l: 'L',
            liter: 'L',
            liters: 'L',
            litre: 'L',
            litres: 'L'
        };
        const pad = 36;
        const width = 1024;
        const baseHeader = 200;
        const rowH = 36;
        const footer = 80;
        const notesLines = (notes && notes.trim()) ? Math.min(6, Math.ceil(notes.trim().length / 60)) : 0;
        const height = baseHeader + (items.length + 2) * rowH + (notesLines ? (notesLines + 1) * rowH : 0) + footer;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = Math.max(480, height);
        const ctx = canvas.getContext('2d');
        // background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        // header
        ctx.fillStyle = '#111827';
        ctx.font = '700 36px Arial';
        ctx.fillText('Purchase Order Draft', pad, pad + 20);
        ctx.font = '400 18px Arial';
        const nowStr = new Date().toLocaleString();
        ctx.fillText(`Generated: ${nowStr}`, pad, pad + 52);
        if (deliveryDate) {
            ctx.fillText(`Requested Delivery: ${deliveryDate}`, pad, pad + 78);
        }
        // category pill
        const catText = `Category: ${categoryLabel}`;
        ctx.font = '600 22px Arial';
        const catW = ctx.measureText(catText).width + 24;
        const catX = pad;
        const catY = pad + 112;
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(catX, catY - 26, catW, 34);
        ctx.strokeStyle = '#e5e7eb';
        ctx.strokeRect(catX, catY - 26, catW, 34);
        ctx.fillStyle = '#111827';
        ctx.fillText(catText, catX + 12, catY);
        // vendor box
        const vX = pad;
        const vY = pad + 150;
        const vW = width - pad * 2;
        const vH = 60;
        ctx.fillStyle = '#f9fafb';
        ctx.fillRect(vX, vY, vW, vH);
        ctx.strokeStyle = '#e5e7eb';
        ctx.strokeRect(vX, vY, vW, vH);
        ctx.fillStyle = '#111827';
        ctx.font = '600 20px Arial';
        const vName = vendor?.name || 'Unassigned Vendor';
        ctx.fillText(`Vendor: ${vName}`, vX + 12, vY + 24);
        ctx.font = '400 16px Arial';
        const vLine2 = [vendor?.contact_phone, vendor?.contact_email].filter(Boolean).join(' · ');
        if (vLine2) ctx.fillText(vLine2, vX + 12, vY + 46);
        // table header
        let y = vY + vH + 32;
        ctx.font = '700 18px Arial';
        ctx.fillStyle = '#374151';
        ctx.fillText('#', pad, y);
        ctx.fillText('Item', pad + 40, y);
        ctx.fillText('Qty', width - pad - 180, y);
        ctx.fillText('Unit', width - pad - 80, y);
        y += 10;
        ctx.strokeStyle = '#e5e7eb';
        ctx.beginPath();
        ctx.moveTo(pad, y);
        ctx.lineTo(width - pad, y);
        ctx.stroke();
        y += 22;
        // table rows
        ctx.font = '400 17px Arial';
        ctx.fillStyle = '#111827';
        items.forEach((it, idx) => {
            const qtyVal = Number(it.reorder_qty || 0).toFixed(2);
            const rawUnit = (it.unit || '').toString();
            const unitVal = unitLabelMap[rawUnit.toLowerCase()] || rawUnit;
            ctx.fillText(String(idx + 1), pad, y);
            // item name with simple truncation
            const maxItemWidth = width - pad * 2 - 260;
            let name = (it.name || '').toString();
            while (ctx.measureText(name).width > maxItemWidth && name.length > 4) {
                name = name.slice(0, -1);
            }
            if (name.length < (it.name || '').length) name += '…';
            ctx.fillText(name, pad + 40, y);
            ctx.fillText(qtyVal, width - pad - 180, y);
            ctx.fillText(unitVal, width - pad - 80, y);
            y += rowH;
        });
        // notes
        if (notes && notes.trim()) {
            y += 8;
            ctx.strokeStyle = '#e5e7eb';
            ctx.beginPath();
            ctx.moveTo(pad, y);
            ctx.lineTo(width - pad, y);
            ctx.stroke();
            y += 28;
            ctx.font = '700 18px Arial';
            ctx.fillStyle = '#374151';
            ctx.fillText('Notes', pad, y);
            y += 26;
            ctx.font = '400 16px Arial';
            ctx.fillStyle = '#111827';
            const words = notes.trim().split(/\s+/);
            let line = '';
            const maxW = width - pad * 2;
            for (let i = 0; i < words.length; i++) {
                const test = line ? line + ' ' + words[i] : words[i];
                if (ctx.measureText(test).width > maxW) {
                    ctx.fillText(line, pad, y);
                    y += rowH;
                    line = words[i];
                } else {
                    line = test;
                }
            }
            if (line) {
                ctx.fillText(line, pad, y);
                y += rowH;
            }
        }
        return canvas.toDataURL('image/png');
    }, []);

    const [draftSuccessInfo, setDraftSuccessInfo] = useState(null);

    const createDraftPdfBlob = useCallback((dataUrl) => {
        if (!dataUrl) return null;

        try {
            const pdf = new jsPDF('p', 'mm', 'a4');
            const pageW = pdf.internal.pageSize.getWidth();
            const pageH = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const maxW = pageW - margin * 2;
            const maxH = pageH - margin * 2;

            const props = pdf.getImageProperties(dataUrl);
            const ratio = props.width / props.height;
            let renderW = maxW;
            let renderH = renderW / ratio;
            if (renderH > maxH) {
                renderH = maxH;
                renderW = renderH * ratio;
            }

            const x = (pageW - renderW) / 2;
            const y = margin;
            pdf.addImage(dataUrl, 'PNG', x, y, renderW, renderH, undefined, 'FAST');

            return pdf.output('blob');
        } catch (err) {
            console.error('Draft PDF generation failed:', err);
            return null;
        }
    }, []);

    const triggerPdfDownloadFromUrl = useCallback((blobUrl, filename) => {
        if (!blobUrl || !filename) return false;
        try {
            if (typeof document === 'undefined') return false;
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return true;
        } catch (err) {
            console.error('Draft PDF download trigger failed:', err);
            return false;
        }
    }, []);

    const openPdfPreview = useCallback((blobUrl) => {
        if (!blobUrl || typeof window === 'undefined') return false;
        try {
            const w = window.open(blobUrl, '_blank', 'noopener,noreferrer');
            return !!w;
        } catch (err) {
            console.error('Draft PDF preview failed:', err);
            return false;
        }
    }, []);

    const closeDraftSuccessModal = useCallback(() => {
        try {
            const files = draftSuccessInfo?.files || [];
            files.forEach((f) => {
                if (f?.url) {
                    window.URL.revokeObjectURL(f.url);
                }
            });
        } catch (err) {
            console.error('Draft PDF URL cleanup failed:', err);
        }
        setDraftSuccessInfo(null);
    }, [draftSuccessInfo]);

    useEffect(() => {
        return () => {
            try {
                const files = draftSuccessInfo?.files || [];
                files.forEach((f) => {
                    if (f?.url) {
                        window.URL.revokeObjectURL(f.url);
                    }
                });
            } catch (_) {
                // no-op
            }
        };
    }, [draftSuccessInfo]);

    const handleSaveDraft = useCallback(() => {
        try {
            const chosen = items
                .filter(x => selected[x.item_id] && (x.reorder_qty || 0) > 0)
                .map((x) => ({
                    ...x,
                    // Respect currently selected unit from the unit dropdown for draft exports.
                    unit: itemUnits[x.item_id] || x.unit
                }));
            if (chosen.length === 0) {
                setError('Please select at least one item before saving draft.');
                return;
            }
            // group by vendor category
            const groups = new Map(); // key -> { label, items: [] }
            chosen.forEach(it => {
                const rawCat = it.smart_vendor_category || it.vendor_category || it.category || 'wholesale';
                const { key, label } = canonicalizeVendorCategory(rawCat);
                const g = groups.get(key) || { key, label, items: [] };
                g.items.push(it);
                groups.set(key, g);
            });
            const ts = new Date();
            const tsName = ts.toISOString().replace(/[:T]/g, '-').split('.')[0];
            const savedMeta = [];
            const pdfFiles = [];
            
            
            groups.forEach(g => {
                
                // STRATEGY 1: Try exact match by normalized category key
                let pv = preferredVendors.find(v => {
                    const vKey = (v.normalizedCategoryKey || '').toString();
                    const matches = vKey === g.key;
                    return matches;
                });
                
                // STRATEGY 2: If no exact match and only ONE preferred vendor exists, use it
                // (This handles cases where user has selected a vendor but category doesn't match exactly)
                if (!pv && preferredVendors.length === 1) {
                    pv = preferredVendors[0];
                }
                
                // STRATEGY 3: Try fuzzy match on vendor category (includes check)
                if (!pv && preferredVendors.length > 1) {
                    pv = preferredVendors.find(v => {
                        const vCat = (v.vendor_category || v.category || '').toString().toLowerCase();
                        const gKeyLower = g.key.toLowerCase();
                        const matches = vCat.includes(gKeyLower) || gKeyLower.includes(vCat);
                        return matches;
                    });
                }
                
                // STRATEGY 4: Search all vendors as final fallback
                if (!pv) {
                    pv = vendors.find(v => {
                        const vCat = (v.vendor_category || v.category || '').toString().toLowerCase();
                        const matches = vCat.includes(g.key);
                        return matches;
                    });
                }
                

                
                const dataUrl = generateBillImage({
                    categoryLabel: g.label,
                    vendor: pv,
                    items: g.items,
                    notes: orderNotes || '',
                    deliveryDate: requestedDeliveryDate || ''
                });
                const pageFilename = `PO_Draft_${g.label}_${tsName}.pdf`;
                pdfFiles.push({
                    categoryKey: g.key,
                    categoryLabel: g.label,
                    filename: pageFilename,
                    dataUrl
                });
                savedMeta.push({
                    categoryKey: g.key,
                    categoryLabel: g.label,
                    vendorId: pv?.vendor_id || null,
                    vendorName: pv?.name || null,
                    filename: pageFilename,
                    createdAt: ts.toISOString()
                });
            });
            
            // persist draft and images metadata
            const draftPayload = {
                items: chosen.map(i => ({ item_id: i.item_id, name: i.name, reorder_qty: i.reorder_qty, unit: i.unit, category: i.smart_vendor_category || i.vendor_category || i.category || 'wholesale' })),
                notes: orderNotes || '',
                requestedDeliveryDate: requestedDeliveryDate || '',
                preferredVendors: preferredVendors || [],
                savedAt: ts.toISOString()
            };
            localStorage.setItem('reorderDraft', JSON.stringify(draftPayload));
            const prevBills = JSON.parse(localStorage.getItem('reorderDraftBills') || '[]');
            localStorage.setItem('reorderDraftBills', JSON.stringify([...(Array.isArray(prevBills) ? prevBills : []), ...savedMeta]));

            // Build PDF blobs, trigger downloads, and keep preview/download links for user fallback.
            const generatedFiles = [];
            let downloadedCount = 0;
            pdfFiles.forEach((entry) => {
                const blob = createDraftPdfBlob(entry.dataUrl);
                if (!blob) return;

                const blobUrl = window.URL.createObjectURL(blob);
                generatedFiles.push({ filename: entry.filename, url: blobUrl });

                const ok = triggerPdfDownloadFromUrl(blobUrl, entry.filename);
                if (ok) downloadedCount += 1;
            });

            if (generatedFiles.length === 0) {
                setError('Failed to generate draft PDF. Please try again.');
                return;
            }

            // Auto-open first PDF preview so user can always view extracted draft format.
            openPdfPreview(generatedFiles[0].url);

            setDraftSuccessInfo({
                mode: 'download',
                count: downloadedCount,
                fileType: 'pdf',
                perCategory: true,
                files: generatedFiles
            });
        } catch (err) {
            console.error('Save draft failed:', err);
            setError('Failed to save draft.');
        }
    }, [items, selected, itemUnits, canonicalizeVendorCategory, preferredVendors, vendors, generateBillImage, orderNotes, requestedDeliveryDate, createDraftPdfBlob, triggerPdfDownloadFromUrl, openPdfPreview]);

    const handleCreateReorder = async () => {
        if (selectedItems.length === 0) {
            setError('Please select at least one item to reorder');
            return;
        }

        setCreating(true);
        setError(null);

        try {
            if (preferredVendors.length === 0) {
                throw new Error('No preferred vendors available. Please add vendors to your preferred list first.');
            }


            let orderResults = [];
            let totalOrderCount = 0;

            // Category-based distribution
            for (let i = 0; i < preferredVendors.length; i++) {
                const vendor = preferredVendors[i];
                const vendorCategoryKey = (vendor.normalizedCategoryKey || vendor.category || vendor.vendor_category || 'wholesale')
                    .toString().trim().toLowerCase();

                const vendorItems = selectedItems.filter(item => {
                    const itemCategory = (item.smart_vendor_category || 'wholesale').toString().trim().toLowerCase();
                    return itemCategory === vendorCategoryKey;
                });

                if (vendorItems.length === 0) continue;

                const businessId = Number(localStorage.getItem('businessId'));
                const orderData = {
                    businessId,
                    vendorId: vendor.vendor_id,
                    selectedItems: vendorItems.map(item => ({
                        itemId: item.item_id,
                        orderQuantity: item.reorder_qty,
                        notes: `Smart categorization: ${item.smart_vendor_category || 'wholesale'}. Current: ${item.current_stock || 'N/A'}, ROP: ${item.reorder_point || 'N/A'}`
                    })),
                    orderNotes: `${orderNotes || ''}\n\nSmart categorized order for ${vendorCategoryKey} vendor. Items: ${vendorItems.map(i => i.name).join(', ')}`.trim(),
                    requestedDeliveryDate,
                    createdBy: businessId
                };

                try {
                    const token = localStorage.getItem('token');
                    const response = await fetch('/api/reorder/create-purchase-order', {
                        method: 'POST',
                        headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(orderData)
                    });
                    const result = await response.json();
            if (result?.success) {
                        orderResults.push({
                            vendor: vendor.name,
                            vendorId: vendor.vendor_id,
                            category: vendor.vendor_category || vendor.category || vendorCategoryKey,
                            poNumber: result.purchaseOrder?.poNumber,
                itemCount: vendorItems.length
                        });
                        totalOrderCount++;
                    }
                } catch (err) {
                    // continue
                }
            }

            // Fallback: broadcast same order to all preferred vendors
            if (orderResults.length === 0) {
                orderResults = [];
                totalOrderCount = 0;

                const businessId = Number(localStorage.getItem('businessId'));
                for (let i = 0; i < preferredVendors.length; i++) {
                    const vendor = preferredVendors[i];
                    const orderData = {
                        businessId,
                        vendorId: vendor.vendor_id,
                        selectedItems: selectedItems.map(item => ({
                            itemId: item.item_id,
                            orderQuantity: item.reorder_qty,
                            notes: `Reorder due to low stock. Current: ${item.current_stock || 'N/A'}, ROP: ${item.reorder_point || 'N/A'}`
                        })),
                        orderNotes: `${orderNotes || ''}\n\nMulti-vendor order - sent to all preferred vendors (${selectedItems.map(i => i.name).join(', ')})`.trim(),
                        requestedDeliveryDate,
                        createdBy: businessId
                    };

                    try {
                        const token = localStorage.getItem('token');
                        const response = await fetch('/api/reorder/create-purchase-order', {
                            method: 'POST',
                            headers: { 
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(orderData)
                        });
                        const result = await response.json();
            if (result?.success) {
                            orderResults.push({
                                vendor: vendor.name,
                                vendorId: vendor.vendor_id,
                                category: vendor.vendor_category || vendor.category || 'All',
                                poNumber: result.purchaseOrder?.poNumber,
                itemCount: selectedItems.length
                            });
                            totalOrderCount++;
                        }
                    } catch (err) {
                        // continue
                    }
                }
            }

            if (orderResults.length === 0) {
                throw new Error('Failed to create orders for any preferred vendors');
            }

            // Final success info
            setSuccessInfo({
                poNumber: orderResults.map(r => r.poNumber).join(', '),
                totalAmount: orderResults.length > 1 ? 'Multiple Orders' : 'Single Order',
                itemCount: orderResults.reduce((total, r) => total + r.itemCount, 0),
                orderCount: totalOrderCount,
                vendorBreakdown: orderResults
            });

        } catch (e) {
            console.error('Error creating reorder:', e);
            setError(e.message || 'Failed to create reorder');
        } finally {
            setCreating(false);
        }
    };

    const preview = useMemo(() => {
        const chosen = items.filter(x => selected[x.item_id] && (x.reorder_qty || 0) > 0);
        if (chosen.length === 0) return 'No items selected.';
        
        const lines = chosen.map(x => `- ${x.name}: ${Number(x.reorder_qty).toFixed(2)} ${x.unit || ''}`);
        
        return [
            'Purchase Order Request - Multi-Vendor Distribution',
            `Date: ${new Date().toLocaleDateString()}`,
            `Vendors: ${preferredVendors.length} preferred vendors (all will receive the same order)`,
            `Delivery Date: ${requestedDeliveryDate || 'Not specified'}`,
            '',
            'Items to Order (sent to ALL preferred vendors):',
            ...lines,
            '',
            `Notes: ${orderNotes || 'None'}`,
            '____________________________________________________'
        ].join('\n');
    }, [items, selected, preferredVendors, requestedDeliveryDate, orderNotes]);

    return (
        <div className="cr-page-container" style={{ paddingBottom: '100px', minHeight: '100vh', backgroundColor: '#fff' }}>
            <StandardScrollStyles />
            
            {/* Header */}
            <div className="cr-header">
                <h1>Create Reorder</h1>
            </div>

            {loading && (
                <div className="cr-loading-overlay">
                    <div style={{
                        display:'flex', flexDirection:'column', alignItems:'center', gap:'20px'
                    }}>
                        <div style={{ position: 'relative', width: '95px', height: '95px' }}>
                            <img 
                                src="/invexis-logo.png"
                                alt="Invexis"
                                loading="eager"
                                fetchpriority="high"
                                style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    width: '70px',
                                    height: '70px',
                                    borderRadius: '50%',
                                    backgroundColor: '#fff',
                                    padding: '0px',
                                    pointerEvents: 'none',
                                    objectFit: 'contain',
                                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                                    zIndex: 1
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '70px',
                                height: '70px',
                                border: '6px solid #000000',
                                borderTop: '6px solid #f9c806',
                                borderRight: '6px solid #f9c806',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                zIndex: 16
                            }}></div>
                        </div>
                        <p style={{
                            color: '#666',
                            fontSize: '16px',
                            textAlign: 'center',
                            margin: 0,
                            marginTop: '-8px'
                        }}>Loading...</p>
                    </div>
                    <style>{`
                        @keyframes spin { 
                            0% { transform: translate(-50%, -50%) rotate(0deg); } 
                            100% { transform: translate(-50%, -50%) rotate(360deg); } 
                        }
                    `}</style>
                </div>
            )}
            
            {/* Success Modal */}
            {successInfo && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(400px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '24px',
                        textAlign: 'center'
                    }}>
                        {/* Green Tick Mark */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            marginBottom: '16px' 
                        }}>
                            <div style={{
                                width: '60px', 
                                height: '60px', 
                                borderRadius: '50%', 
                                border: '3px solid #22c55e',
                                backgroundColor: 'white',
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center'
                            }}>
                                <Check size={36} color="#22c55e" strokeWidth={3} />
                            </div>
                        </div>
                        
                        {/* Success Message */}
                        <div style={{ 
                            fontSize: '18px', 
                            fontWeight: '600', 
                            marginBottom: '16px',
                            color: '#1f2937'
                        }}>
                            {successInfo.orderCount > 1 
                                ? `${successInfo.orderCount} Purchase Orders Created Successfully`
                                : 'Purchase Order Created Successfully'
                            }
                        </div>
                        
                        {/* Order Details */}
                        <div style={{ 
                            color: '#4b5563', 
                            lineHeight: '1.6', 
                            fontSize: '14px', 
                            marginBottom: '20px' 
                        }}>
                            <div style={{ marginBottom: '8px' }}>
                                <strong>Total Items:</strong> {successInfo.itemCount}
                                {successInfo.orderCount > 1 && (
                                    <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                                        across {successInfo.orderCount} vendors
                                    </span>
                                )}
                            </div>

                            {/* Vendor Breakdown for Multiple Orders */}
                            {successInfo.vendorBreakdown && successInfo.vendorBreakdown.length > 1 && (
                                <div style={{ 
                                    marginTop: '12px', 
                                    padding: '10px',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb'
                                }}>
                                    <div style={{ 
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        marginBottom: '8px',
                                        color: '#374151'
                                    }}>
                                        📋 Order Breakdown:
                                    </div>
                                    {successInfo.vendorBreakdown.map((order, index) => (
                                        <div key={index} style={{ 
                                            fontSize: '11px',
                                            marginBottom: '4px',
                                            color: '#6b7280'
                                        }}>
                                            • <strong>{order.vendor}</strong> ({order.category}): {order.itemCount} items - {order.poNumber}
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {/* WhatsApp integration removed */}
                        </div>
                        
                        {/* OK Button */}
                        <button
                            onClick={() => {
                                // Reset form when user clicks OK
                                setSelected({});
                                setOrderNotes('');
                                // Close popup
                                setSuccessInfo(null);
                            }}
                            style={{
                                padding: '10px 24px', 
                                backgroundColor: '#22c55e', 
                                color: '#fff',
                                border: 'none', 
                                borderRadius: '8px', 
                                fontSize: '14px', 
                                fontWeight: '600',
                                cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Save Draft Success Modal */}
            {draftSuccessInfo && (
                <div style={{
                    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
                }}>
                    <div style={{
                        width: 'min(380px, 90vw)', background: '#fff', borderRadius: 12,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)', padding: '24px',
                        textAlign: 'center'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '50%', border: '3px solid #22c55e',
                                backgroundColor: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <Check size={36} color="#22c55e" strokeWidth={3} />
                            </div>
                        </div>
                        <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
                            Draft Saved Successfully
                        </div>
                        <div style={{ color: '#4b5563', fontSize: '13px', marginBottom: '16px' }}>
                            {draftSuccessInfo.fileType === 'pdf'
                                ? `${(draftSuccessInfo.files || []).length || draftSuccessInfo.count || 0} PDF${(((draftSuccessInfo.files || []).length || draftSuccessInfo.count || 0) === 1) ? '' : 's'} generated`
                                : `${draftSuccessInfo.count || 0} image(s) ${draftSuccessInfo.mode === 'shared' ? 'shared' : draftSuccessInfo.mode === 'folder' ? 'saved' : 'downloaded'}`}
                        </div>
                        {draftSuccessInfo.fileType === 'pdf' && Array.isArray(draftSuccessInfo.files) && draftSuccessInfo.files.length > 0 && (
                            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <button
                                    onClick={() => {
                                        const first = draftSuccessInfo.files[0];
                                        if (first?.url) {
                                            window.open(first.url, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    style={{
                                        padding: '10px 16px', backgroundColor: '#111827', color: '#fff', border: 'none',
                                        borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    Open PDF Preview
                                </button>
                                <button
                                    onClick={() => {
                                        draftSuccessInfo.files.forEach((f) => {
                                            if (!f?.url || !f?.filename) return;
                                            const link = document.createElement('a');
                                            link.href = f.url;
                                            link.download = f.filename;
                                            link.style.display = 'none';
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        });
                                    }}
                                    style={{
                                        padding: '10px 16px', backgroundColor: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db',
                                        borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer'
                                    }}
                                >
                                    Download PDF Again
                                </button>
                            </div>
                        )}
                        <button
                            onClick={closeDraftSuccessModal}
                            style={{
                                padding: '10px 24px', backgroundColor: '#000000', color: '#fff', border: 'none',
                                borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
                            }}
                        >
                            OK
                        </button>
                    </div>
                </div>
            )}

            {error && <div style={{ color: 'red', marginBottom: 16 }}>{error}</div>}

            {!loading && !error && (
                <>
                    {/* Critical Low Stock Items */}
                    <div className="cr-section">
                        <h2 className="cr-section-title" style={{ paddingLeft: '0 !important', marginLeft: '-16px', textTransform: 'capitalize' }}>
                            Critical Low Stock Items
                        </h2>
                        <div className="cr-low-stock-card">
                            {criticalItems.map(item => {
                                const stockLevel = Number(item.current_stock || 0);
                                const qty = formatQtyWithWords(stockLevel, item.unit);
                                const reorderBase = Number(item.reorder_point ?? item.minimum_stock_level ?? 0);
                                const safetyBase = Number(item.safety_stock ?? item.maximum_stock_level ?? 0);
                                const minimalBase = reorderBase > 0 ? reorderBase : safetyBase;
                                const minQty = formatQtyWithWords(minimalBase, item.unit);
                                const isCritical = safetyBase > 0
                                    ? stockLevel <= safetyBase
                                    : (reorderBase > 0 ? stockLevel <= reorderBase : false);
                                
                                return (
                                    <div
                                        key={item.item_id}
                                        className={`cr-low-stock-item ${isCritical ? 'cr-alert-critical' : 'cr-alert-warning'}`}
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            if (typeof goTo === 'function') {
                                                goTo('xyz');
                                            } else {
                                                window.dispatchEvent(new CustomEvent('navigate', { detail: { screen: 'xyz' } }));
                                            }
                                        }}
                                    >
                                        <div className="cr-alert-icon">
                                            <Package size={20} strokeWidth={2} />
                                        </div>
                                        <div className="cr-alert-content">
                                            <p className="cr-alert-title">
                                                {isCritical ? 'Critical Stock' : 'Low Stock'}: {item.name}
                                            </p>
                                            <div className="cr-alert-bullets-wrapper">
                                                <p className="cr-alert-description cr-alert-bullet">• Current stock: {qty.value} {qty.unit}</p>
                                                <p className="cr-alert-minimum cr-alert-bullet">• Minimum stock: {minQty.value} {minQty.unit}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                            {criticalItems.length === 0 && (
                                <div style={{ 
                                    padding: '32px 16px', 
                                    textAlign: 'center', 
                                    color: 'var(--secondary-text)',
                                    backgroundColor: '#f8fafc',
                                    borderRadius: '8px',
                                    border: '2px dashed #e2e8f0',
                                    flex: '0 0 auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉</div>
                                    <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#059669' }}>
                                        Great News!
                                    </div>
                                    <div style={{ fontSize: '14px' }}>
                                        All items are well stocked above reorder points
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Kitchen leakage — the last CALCULATED month, or the
                        prompt to calculate it. The tile never shows a live
                        estimate: a month is a locked record (spec N5), so
                        "nothing yet" is the honest state before the run. */}
                    {leakageTile && (
                        <div style={{ margin: '8px 0 16px 0' }}>
                            <button
                                type="button"
                                className="cr-leakage-banner-btn"
                                onClick={() => goTo && goTo('leakage-monthly',
                                    { month: leakageTile.month, from: 'create-reorder' })}
                            >
                                <div className="cr-leakage-banner-left">
                                    <div className="cr-leakage-icon-badge">
                                        <TrendingDown size={22} />
                                    </div>
                                    <div>
                                        <h4 className="cr-leakage-banner-title">Kitchen Leakage Tracker</h4>
                                        <p className="cr-leakage-banner-sub">{leakageTile.subtitle}</p>
                                    </div>
                                </div>
                                <div className="cr-leakage-banner-action">
                                    <span>{leakageTile.action}</span>
                                    <ChevronRight size={16} />
                                </div>
                            </button>
                        </div>
                    )}

                    {/* Preferred Vendors */}
                    <div className="cr-section">
                        <h2 className="cr-section-title" style={{ margin: '0 0 12px -16px', padding: 0, textAlign: 'left', textTransform: 'capitalize' }}>Preferred Vendors</h2>
                        {preferredVendors.length > 0 && console.log('   Vendors:', preferredVendors.map(v => v.name).join(', '))}
                        <div className="cr-preferred-vendors-container">
                            {/* Dynamic Vendor Rendering */}
                            {preferredVendors.map((vendor, index) => {console.log(`   Rendering vendor ${index + 1}:`, vendor.name); return (
                                <div key={`${vendor.vendor_id}-${vendor.category}-${index}`} className="cr-vendor-card">
                                    <div className="cr-vendor-info">
                                        <h3 style={{ textTransform: 'capitalize' }}>{vendor.name}</h3>
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: 'var(--secondary-text)', 
                                            marginBottom: '8px',
                                            fontWeight: '500',
                                            textAlign: 'left',
                                            textTransform: 'capitalize'
                                        }}>
                                            {vendor.vendor_category || vendor.category || 'Other'}
                                        </div>
                                        <div className="cr-vendor-rating">
                                            {[...Array(5)].map((_, i) => (
                                                <Star 
                                                    key={i} 
                                                    size={14} 
                                                    fill={i < Math.floor(Number(vendor.average_rating || 0)) ? "#ffc107" : "none"}
                                                    color="#ffc107"
                                                />
                                            ))}
                                            <span>({vendor.total_purchase_orders || '0'} orders)</span>
                                        </div>
                                        {vendor.contact_phone && (
                                            <div className="cr-vendor-contact">
                                                <Phone size={16} />
                                                <span>{vendor.contact_phone}</span>
                                            </div>
                                        )}
                                        {vendor.contact_email && (
                                            <div className="cr-vendor-contact">
                                                <Mail size={16} />
                                                <span>{vendor.contact_email}</span>
                                            </div>
                                        )}
                                    </div>
                                    <button 
                                        className="cr-change-btn"
                                        onClick={() => {
                                            onNavigateToVendor(normalizeCategoryName(vendor.vendor_category || vendor.category));
                                        }}
                                    >
                                        Change
                                    </button>
                                </div>
                            );
                            })}

                            {/* No vendors fallback - only show if no vendors */}
                            {preferredVendors.length === 0 && (
                                <div style={{ 
                                    padding: '20px', 
                                    textAlign: 'center', 
                                    color: 'var(--secondary-text)',
                                    width: '100%'
                                }}>
                                    <p>{t('reorder.no_preferred_vendors')}</p>
                                    <button 
                                        className="cr-change-btn"
                                        onClick={() => {
                                            onNavigateToVendor();
                                        }}
                                        style={{ marginTop: '12px' }}
                                    >
                                        Browse All Vendors
                                    </button>
                                </div>
                            )}
                        </div>
                        
                        {/* Add Vendors Button */}
                        <button 
                            className="cr-add-more-btn"
                            onClick={() => onNavigateToVendor && onNavigateToVendor('All')}
                        >
                            <Plus size={20} />
                            Add Vendors
                        </button>
                    </div>

                    {/* Order Items */}
                    <div className="cr-section">
                        <h2 className="cr-section-title" style={{ margin: '0 0 12px -16px', padding: 0, textAlign: 'left' }}>Order Items</h2>
                        <div
                            className={`cr-order-list-container ${items.length === 1 ? 'cr-single-card' : ''}`}
                            style={standardScrollStyles}
                        >
                            {items.map(item => {
                                // Removed 'Recommended' severity logic and badge
                                return (
                                <div key={item.item_id} className="cr-order-item-card" data-item-id={item.item_id} style={{ position: 'relative' }}>
                                    {/* Newly badge if item.is_newly_added */}
                                    {item.is_newly_added && (
                                        <div style={{
                                            position: 'absolute',
                                            top: 5,
                                            right: 5,
                                            background: '#fff',
                                            color: 'rgb(150 91 250)',
                                            border: '2px solid rgb(150 91 250)',
                                            borderRadius: 6,
                                            padding: '2px 12px',
                                            fontSize: 12,
                                            fontWeight: 700,
                                            zIndex: 2,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
                                        }}>
                                            New
                                        </div>
                                    )}
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px', gap: 8 }}>
                                            <p className="cr-item-name" style={defaultTextSizes}>{item.name}</p>
                                            {/* Removed Recommended badge */}
                                            {/* Removed extra orange 'NEWLY ADDED ITEM' badge */}
                                        </div>
                                        <div className="cr-stock-info-container">
                                            <span className="cr-item-stock" style={{ ...defaultTextSizes, fontSize: '0.9em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                Current stock: {formatStockDisplay(item.current_stock, item.unit)}
                                            </span>
                                            <span className="cr-item-stock" style={{ ...defaultTextSizes, fontSize: '0.9em', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                Reorder point: {formatStockDisplay(item.reorder_point ?? item.minimum_stock_level ?? 0, item.unit)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="cr-quantity-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <button 
                                            onClick={() => {
                                                const newQty = Math.max(0, (item.reorder_qty || 0) - 1);
                                                updateQty(item.item_id, newQty);
                                                if (newQty === 0) {
                                                    setSelected(prev => ({ ...prev, [item.item_id]: false }));
                                                } else {
                                                    setSelected(prev => ({ ...prev, [item.item_id]: true }));
                                                }
                                            }}
                                        >
                                            −
                                        </button>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.reorder_qty > 0 ? item.reorder_qty : ''}
                                            placeholder="0"
                                            onChange={(e) => {
                                                const newQty = Math.max(0, parseFloat(e.target.value || '0'));
                                                updateQty(item.item_id, newQty);
                                                if (newQty === 0) {
                                                    setSelected(prev => ({ ...prev, [item.item_id]: false }));
                                                } else {
                                                    setSelected(prev => ({ ...prev, [item.item_id]: true }));
                                                }
                                            }}
                                        />
                                        <button 
                                            onClick={() => {
                                                const newQty = (item.reorder_qty || 0) + 1;
                                                updateQty(item.item_id, newQty);
                                                setSelected(prev => ({ ...prev, [item.item_id]: true }));
                                            }}
                                        >
                                            +
                                        </button>
                                        {item.unit && (() => {
                                            const unitOptions = getUnitOptions(item.unit);
                                            // Show dropdown only if unit has conversions available
                                            if (unitOptions && unitOptions.length > 1) {
                                                const currentUnit = itemUnits[item.item_id] || item.unit;
                                                const selectedOption = unitOptions.find(option => option.value === currentUnit);
                                                const isUnitDropdownOpen = String(openUnitDropdownId) === String(item.item_id);

                                                return (
                                                    <div className={`cr-unit-dropdown-wrapper ${isUnitDropdownOpen ? 'open' : ''}`}>
                                                        <button
                                                            type="button"
                                                            className={`cr-unit-trigger ${isUnitDropdownOpen ? 'open' : ''}`}
                                                            ref={(el) => {
                                                                const key = String(item.item_id);
                                                                if (el) {
                                                                    unitTriggerRefs.current[key] = el;
                                                                } else {
                                                                    delete unitTriggerRefs.current[key];
                                                                }
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                const isOpen = String(openUnitDropdownId) === String(item.item_id);
                                                                if (isOpen) {
                                                                    setOpenUnitDropdownId(null);
                                                                    setUnitDropdownMenuStyle(null);
                                                                    return;
                                                                }

                                                                positionUnitDropdownMenu(item.item_id, unitOptions.length);
                                                                setOpenUnitDropdownId(item.item_id);
                                                            }}
                                                            aria-haspopup="listbox"
                                                            aria-expanded={isUnitDropdownOpen}
                                                        >
                                                            <span className="cr-unit-trigger-label">
                                                                {selectedOption ? selectedOption.label : getShortUnit(currentUnit)}
                                                            </span>
                                                            <span className="cr-unit-trigger-chevron" />
                                                        </button>

                                                        {isUnitDropdownOpen && typeof document !== 'undefined' && createPortal(
                                                            <div
                                                                className="cr-unit-menu cr-unit-menu-portal"
                                                                style={unitDropdownMenuStyle || undefined}
                                                                role="listbox"
                                                                onMouseDown={(e) => e.stopPropagation()}
                                                            >
                                                                {unitOptions.map(option => (
                                                                    <button
                                                                        key={option.value}
                                                                        type="button"
                                                                        className={`cr-unit-option ${currentUnit === option.value ? 'selected' : ''}`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleUnitChange(item.item_id, option.value, item.reorder_qty, currentUnit);
                                                                            setOpenUnitDropdownId(null);
                                                                            setUnitDropdownMenuStyle(null);
                                                                        }}
                                                                    >
                                                                        {option.label}
                                                                    </button>
                                                                ))}
                                                            </div>,
                                                            document.body
                                                        )}
                                                    </div>
                                                );
                                            }
                                            // For non-convertible units, show plain text
                                            return (
                                                <span style={{ 
                                                    fontSize: '0.9em', 
                                                    color: '#6b7280',
                                                    fontWeight: '500',
                                                    whiteSpace: 'nowrap',
                                                    minWidth: '35px',
                                                    textAlign: 'left'
                                                }}>
                                                    {getShortUnit(item.unit)}
                                                </span>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )})}
                            
                            {/* Empty State for New Businesses */}
                            {items.length === 0 && (
                                <div style={{ 
                                    padding: '40px 20px', 
                                    textAlign: 'center', 
                                    color: '#6b7280',
                                    backgroundColor: '#f9fafb',
                                    borderRadius: '12px',
                                    border: '2px dashed #e5e7eb'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                                    <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                                        No Inventory Items Yet
                                    </div>
                                    <div style={{ fontSize: '14px', marginBottom: '16px', lineHeight: '1.5' }}>
                                        Add inventory items in the Inventory section first,<br />
                                        then they'll appear here for ordering.
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (typeof goTo === 'function') {
                                                goTo('xyz');
                                            } else {
                                                window.dispatchEvent(new CustomEvent('navigate', { detail: { screen: 'xyz' } }));
                                            }
                                        }}
                                        style={{
                                            padding: '10px 24px',
                                            backgroundColor: '#000',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            marginTop: '8px'
                                        }}
                                    >
                                        Go to Inventory
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Search Bar and Results - appears above button when expanded */}
                        {showInlineAdd && (
                            <div className="cr-inline-add-container">
                                <div className="cr-inline-add-header">
                                    <div className="cr-inline-add-search-wrapper">
                                        <Search size={16} />
                                        <input
                                            type="text"
                                            placeholder="Search Ingredient"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </div>
                                    <button 
                                        className="cr-inline-add-close-btn"
                                        onClick={() => {
                                            setShowInlineAdd(false);
                                            setSearchTerm('');
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>

                                {/* Search Results */}
                                {searchTerm ? (
                                    <div className="cr-inline-add-list" style={standardScrollStyles}>
                                        {filteredAvailableItems.length > 0 ? (
                                            filteredAvailableItems.map(item => {
                                                const isInOrder = items.find(x => x.item_id === item.item_id);
                                                
                                                return (
                                                    <div
                                                        key={item.item_id}
                                                        className="cr-inline-add-item"
                                                        onClick={() => addItemToOrder(item)}
                                                    >
                                                        <span style={defaultTextSizes}>
                                                            {searchTerm 
                                                                ? highlightMatch(item.name, searchTerm)
                                                                : item.name
                                                            }
                                                            {isInOrder && (
                                                                <small style={{ color: '#ff9800', marginLeft: '8px', ...defaultTextSizes }}>
                                                                    (In order)
                                                                </small>
                                                            )}
                                                        </span>
                                                        <button className="cr-inline-add-plus-btn">
                                                            +
                                                        </button>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div className="cr-inline-add-no-items">No Ingredients Found</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="cr-inline-add-no-items">Start Typing To Search Ingredients</div>
                                )}
                            </div>
                        )}

                        {/* Add More Items Button */}
                        <button 
                            className="cr-add-more-btn"
                            onClick={() => setShowInlineAdd(!showInlineAdd)}
                        >
                            <Plus size={16} />
                            {t('reorder.add_more_items')}
                        </button>
                    </div>

                    {/* Additional Order Details */}
                    {/* <div className="cr-section">
                        <h2 className="cr-section-title" style={{ margin: '0 0 12px -16px', padding: 0, textAlign: 'left' }}>Order Details</h2>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, color: 'var(--secondary-text)', marginBottom: 4 }}>
                                    Requested Delivery Date
                                </label>
                                <input 
                                    type="date" 
                                    value={requestedDeliveryDate}
                                    onChange={(e) => setRequestedDeliveryDate(e.target.value)}
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px', 
                                        border: '1px solid var(--border-color)', 
                                        borderRadius: 8,
                                        fontSize: 14
                                    }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', fontSize: 12, color: 'var(--secondary-text)', marginBottom: 4 }}>
                                    Order Notes
                                </label>
                                <textarea 
                                    value={orderNotes}
                                    onChange={(e) => setOrderNotes(e.target.value)}
                                    placeholder="Add Any Special Instructions..."
                                    rows="3"
                                    style={{ 
                                        width: '100%', 
                                        padding: '12px', 
                                        border: '1px solid var(--border-color)', 
                                        borderRadius: 8,
                                        fontSize: 14,
                                        resize: 'vertical'
                                    }}
                                />
                            </div>
                        </div>
                    </div> */}

                    {/* Action Buttons */}
                    <div className="cr-actions">
                        <button 
                            className="cr-draft-btn"
                            onClick={handleSaveDraft}
                        >
                            {t('reorder.save_draft')}
                        </button>
                        <button 
                            className="cr-review-btn"
                            onClick={handleCreateReorder}
                            disabled={true}
                            style={{ 
                                opacity: 0.6,
                                cursor: 'not-allowed'
                            }}
                        >
                            {creating ? t('reorder.creating_orders') : t('reorder.review_send')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

// ⛔ INTENTIONAL PRODUCT DECISION — DO NOT ADD A FORMULA HERE. ⛔
// Suggested/order quantity is a HARD 0, always. The counter starts blank
// (showing the faint "0" placeholder) and the user types the amount. It must
// NEVER be re-derived from stock, reorder point, safety stock, or a
// recommended-order value — doing so pre-fills the boxes and auto-selects
// items before any data is entered, which is exactly the bug this replaced.
// The signature (with the ignored args) is kept only so every existing call
// site stays valid. If you think you need a formula here, you don't — the
// user enters the quantity.
function computeSuggestedQty(_x, _recommendedQty = null) {
    return 0;
}

export default CreateReorder;
